import { type ReactElement, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useDeletePipeline, usePipelineDetailQuery } from '@/features/pipelines';
import { usePipelineDetailPageViewModel } from '@/features/pipelines/hooks/usePipelineDetailPageViewModel';
import { useProjectFilter } from '@/features/projects';
import { useEntityDetailPageScaffold } from '@/features/shared/hooks/useEntityDetailPageScaffold';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { PipelineDetailContent } from './PipelineDetailContent';
import type { AuthSession } from '@/auth/types';

type PipelineDetailPageProps = { session: AuthSession | null };

export const PipelineDetailPage = ({ session }: PipelineDetailPageProps): ReactElement => {
  const { pipelineSlug } = useParams<{ pipelineSlug: string }>();
  const { selectedOrganisationId } = useOrganisationFilter();
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const deletePipeline = useDeletePipeline();
  const searchSlug = useMemo(() => (pipelineSlug ?? '').trim(), [pipelineSlug]);
  const pipelineQuery = usePipelineDetailQuery(searchSlug || null);
  const { data: pipeline, isPending, isError, error } = pipelineQuery;
  const hasInvalidSlug = !searchSlug;
  const detailPage = useEntityDetailPageScaffold({
    load: {
      data: pipeline,
      isPending,
      isFetched: pipelineQuery.isFetched,
      hasInvalidParams: hasInvalidSlug,
    },
    delete: {
      mutation: deletePipeline,
      successMessage: 'Pipeline deleted.',
      fallbackErrorMessage: 'Failed to delete pipeline.',
    },
    redirects: {
      onDeleteTo: '/pipelines',
      notFoundBackLabel: 'Back to pipelines',
    },
    notFoundCopy: {
      title: 'Pipeline not found',
      body: 'This pipeline does not exist or you no longer have access to it.',
    },
  });
  const viewModel = usePipelineDetailPageViewModel({ pipeline });

  useEffect(() => {
    if (!pipeline?.projectId) return;
    if (selectedProjectId && pipeline.projectId !== selectedProjectId) {
      void navigate('/pipelines', { replace: true });
      return;
    }
    if (!selectedProjectId) {
      setSelectedProjectId(pipeline.projectId);
    }
  }, [pipeline, navigate, selectedProjectId, setSelectedProjectId]);

  if (detailPage.showNotFound) return detailPage.renderNotFound();

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view pipeline details."
      organisationId={selectedOrganisationId}
      requireProject={false}
      projectMessage="Select a project to view pipeline details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load pipeline"
      data={pipeline}
    >
      {(resolvedPipeline) => (
        <PipelineDetailContent
          pipeline={resolvedPipeline}
          viewModel={viewModel}
          onDelete={detailPage.handleDelete}
          deleteLoading={detailPage.deleteLoading}
        />
      )}
    </DetailPageScaffold>
  );
};
